import {SlideToggle} from "./SlideToggle.tsx";
import {IconLocationPin} from "./svg-icons/IconLocationPin.tsx";
import {IconCalendarFilter} from "./svg-icons/IconCalendarFilter.tsx";
import {Dropdown} from "./Dropdown.tsx";
import {IconFilter} from "./svg-icons/IconFilter.tsx";
import {DropdownOptions, DropdownPayload, PrefillFiltersProps, SearchFilters} from "../types/travel.ts";
import {ActiveFilters} from "./ActiveFilters.tsx";
import {useRef, useState, useEffect} from "preact/hooks";
import {buildSearchUrl} from "../utils/queryString.ts";

interface GridHeaderProps {
    filters: SearchFilters,
    total: number,
    terminTotal: number,
    gridViewState: boolean,
    setGridViewState: (value: boolean) => void
    onSortChange: (payload : DropdownPayload) => void
    isFilterOverlayOpen?: boolean
    setIsFilterOverlayOpen?: (isOpen: boolean) => void
    currentSortBy?: string
    onFiltersChange: (filters: SearchFilters) => void
    alternativeTexts?: Record<string, string>
    prefillFilters?: PrefillFiltersProps
    showSearchUrl?: boolean
    isSearchUrlOpen?: boolean
    setIsSearchUrlOpen?: (isOpen: boolean) => void
}

export function GridHeader({
                               filters,
                               total,
                               terminTotal,
                               gridViewState,
                               setGridViewState,
                               onSortChange,
                               isFilterOverlayOpen,
                               setIsFilterOverlayOpen,
                               currentSortBy,
                               onFiltersChange,
                               alternativeTexts,
                               prefillFilters,
                               showSearchUrl,
                               isSearchUrlOpen,
                               setIsSearchUrlOpen
                        } : GridHeaderProps) {

    const headerRef = useRef<HTMLDivElement>(null);

    const [headingTag, setHeadingTag] = useState<'h1' | 'h2'>('h2');

    useEffect(() => {
        if (!document.querySelector('h1')) {
            setHeadingTag('h1');
        }
    }, []);

    const Heading = headingTag as 'h1' | 'h2';

    // Editor helper: shows the search URL for the current filters (users with edit_posts, set by the block).
    // Open state lives in ResultsGrid, because GridHeader unmounts while results are loading
    const [isSearchUrlCopied, setIsSearchUrlCopied] = useState(false);

    const searchUrl = showSearchUrl ? buildSearchUrl(filters, gridViewState) : '';

    useEffect(() => {
        setIsSearchUrlCopied(false);
    }, [searchUrl]);

    const handleCopySearchUrl = async () => {
        try {
            await navigator.clipboard.writeText(searchUrl);
            setIsSearchUrlCopied(true);
        } catch (e) {
            console.warn('Cannot copy search url', e);
        }
    }

    const handleChangeFilterOverlayState = () => {
        if(typeof setIsFilterOverlayOpen === 'function') {
            setIsFilterOverlayOpen(!isFilterOverlayOpen)
        }
    }

    const switchSingularPlural = (total: number, type: 'reise' | 'termin'): string => {
        const data = {
            'reise': ['Reise', 'Reisen'],
            'termin': ['Termin', 'Termine']
        }
        return total > 1 ? data[type][1] : data[type][0];
    }

    const excludedKeys = ['start', 'end', 'yearmonth', 'destination']
    const getPrefillCount = (key: string): number => {
        const prefill = prefillFilters?.[key as keyof PrefillFiltersProps] as { selected?: string[] } | undefined
        if (prefill && Array.isArray(prefill.selected)) return prefill.selected.length
        if (key === 'difficulty' && prefillFilters?.tourencharakter?.selected) return 1
        return 0
    }
    const filterCount = Object.entries(filters)
        .filter(([key]) => !excludedKeys.includes(key))
        .reduce((sum, [key, val]) => sum + Math.max(0, (Array.isArray(val) ? val.length : 0) - getPrefillCount(key)), 0);

    const mobileSearchFilterButtonText = (filterCount : number) => {
        if (filterCount > 0) {
            return `Filter (${filterCount})`;
        } else {
            if (gridViewState) {
                return `Filtern & Sortieren`
            } else {
                return `Filtern`
            }
        }
    }

    const reisenLabel = alternativeTexts?.reisen_gefunden
        ? alternativeTexts.reisen_gefunden.replace('%s', String(total))
        : `${total} ${switchSingularPlural(total, 'reise')} gefunden`;

    const termineLabel = alternativeTexts?.termine_gefunden
        ? alternativeTexts.termine_gefunden.replace('%s', String(terminTotal))
        : `${terminTotal} ${switchSingularPlural(terminTotal, 'termin')} gefunden`;

    const text = (!gridViewState && terminTotal === 0)
        ? 'Keine Reise gefunden'
        : gridViewState ? reisenLabel : termineLabel;

    return(
        <>
            <div ref={headerRef} id="grid-header-scroll-target" class={`flex lg:justify-between lg:items-center lg:gap-3 flex-col lg:flex-row`}>
                <Heading className="mb-6 leading-none hidden lg:block text-fluid-mi-18-ma-28">{text}</Heading>
                <div className="mb-7 inline-flex items-center lg:gap-3 justify-between lg:justify-end">
                    <SlideToggle
                        gridViewState={gridViewState}
                        setGridViewState={setGridViewState}
                        leftLabel="Reisen"
                        rightLabel="Termine"
                        iconLeft={<IconLocationPin cssClasses="h-5" />}
                        iconRight={<IconCalendarFilter cssClasses="h-5" />}
                        hideTextOnMobile={true}
                        cssClass={'w-full max-w-[120px] sm:max-w-[150px] lg:max-w-full lg:w-auto order-1 lg:order-2 lg:ml-3'}
                    />
                    {gridViewState && (
                        <Dropdown
                            options={[
                                {value: 'customsort', label: 'Empfehlung'},
                                {value: 'preis', label: 'Niedrigster Preis'},
                                {value: 'anreise', label: 'Frühester Reisebeginn'},
                                {value: 'wertschoepfung', label: 'Höchste lokale Wertschöpfung'},
                            ]}
                            icon={<IconFilter cssClasses="h-4" />}
                            selectText="Bitte Filter wählen"
                            onSelectionChange={(payload:DropdownOptions) => {
                                onSortChange(payload)
                            }}
                            cssClasses="hidden lg:inline-flex order-2 lg:order-1"
                            section="sort-desktop"
                            selectedValue={currentSortBy}
                        />
                    )}

                    <button className="w-full ml-3 block lg:hidden order-2">
                                <span
                                    className={
                                        `h-[43px] w-auto px-[14px] py-[14px] rounded-10px
                                            border-2 border-theme-gray-light flex items-center
                                            gap-2 cursor-pointer whitespace-nowrap justify-center`
                                    }
                                    onClick={() => handleChangeFilterOverlayState()}
                                >
                                    <IconFilter cssClasses="h-4" />
                                    <span className="text-13px font-semibold leading-none">
                                        {mobileSearchFilterButtonText(filterCount)}
                                    </span>
                                </span>
                    </button>

                </div>
            </div>

            {showSearchUrl && (
                <div className="mb-5 text-13px">
                    <button
                        type="button"
                        className="underline cursor-pointer"
                        aria-expanded={isSearchUrlOpen}
                        onClick={() => setIsSearchUrlOpen?.(!isSearchUrlOpen)}
                    >
                        {isSearchUrlOpen ? 'Such-URL ausblenden' : 'Such-URL anzeigen'}
                    </button>
                    {isSearchUrlOpen && (
                        <div className="mt-2 flex gap-2 items-center">
                            <input
                                type="text"
                                readOnly
                                value={searchUrl}
                                aria-label="Such-URL"
                                className="flex-1 min-w-0 px-3 py-2 rounded-10px border-2 border-theme-gray-light"
                                onFocus={(e) => (e.currentTarget as HTMLInputElement).select()}
                            />
                            <button
                                type="button"
                                className="px-3 py-2 rounded-10px border-2 border-theme-gray-light font-semibold whitespace-nowrap cursor-pointer"
                                onClick={handleCopySearchUrl}
                            >
                                {isSearchUrlCopied ? 'Kopiert' : 'Kopieren'}
                            </button>
                        </div>
                    )}
                </div>
            )}

            <ActiveFilters
                filters={filters}
                onFiltersChange={onFiltersChange}
                cssClasses={`hidden lg:block`}
                prefillFilters={prefillFilters}
            />

        </>
    )
}
